import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { customFetchV2 } from '../services/custom-fetch-v2';

export const METERS_PER_MILE = 1609;
const BASE_URL = 'https://api.admin.shieldgps.com';
// The request to fetch odometer history
interface GetDeviceOdometerHistoryRequestProps {
  imei: string;
  startAt?: Date,
  endAt?: Date,
  granularity?: string,
  timezone?: string,
}

enum Granularity {
  MINUTES = 'MINUTES',
  HOURS = 'HOURS',
  DAYS = 'DAYS',
  MONTHS = 'MONTHS',
}

interface OdometerHistoryDatapoint {
  key: string;
  numDatapoints: number;
  distanceMeters: number;
}

// Response from odometer history API
interface GetOdometerHistoryResponse {
  startAt: Date,
  endAt: Date,
  queryTimeMs: number,
  granularity: Granularity,
  timezone: string,
  data: OdometerHistoryDatapoint[],
}

/**
 * API query to retrieve device odometer.
 */
const getDeviceOdometerHistory = async ({
  imei, startAt, endAt, granularity, timezone,
}: GetDeviceOdometerHistoryRequestProps): Promise<GetOdometerHistoryResponse | null> => {
  let url = `${BASE_URL}/devices/${imei}/odometer/history?`;
  if (startAt) {
    url += `&startAt=${startAt.toISOString()}`;
  }

  if (endAt) {
    url += `&endAt=${endAt.toISOString()}`;
  }

  if (granularity) {
    url += `&granularity=${granularity}`;
  }

  if (timezone) {
    url += `&timezone=${timezone}`;
  }

  const response = await customFetchV2<GetOdometerHistoryResponse>(url, {
    method: 'GET',
    withCredentials: false,
  });

  if (!response.ok) {
    throw new Error('Unable to load odometer history');
  }

  return response.data;
};

/**
 * React hook that will fetch and refresh a device odometer.
 */
const useDeviceOdometerHistory = (requestProps: GetDeviceOdometerHistoryRequestProps):
  UseQueryResult<GetOdometerHistoryResponse, unknown> => useQuery(
  ['useDeviceOdometerHistory', requestProps],
  () => getDeviceOdometerHistory(requestProps),
  {
    enabled: !!requestProps,
    retry: false,
    refetchInterval: 10000,
    refetchIntervalInBackground: true,
  },
);

export {
  useDeviceOdometerHistory,
  OdometerHistoryDatapoint,
  GetOdometerHistoryResponse,
};
